/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import { Typography } from '@mui/material';
import { TextFieldControl, InputLabel, AddressAutocompleteControl, CheckboxesGroupControl, FormButtons, PronounSelectControl } from 'components/elements';
import { useFetchPreferredCommunications, useReactHookFormErrorMap } from 'hooks';
import Styles from './styles';

const useStyles = makeStyles(Styles);

function ProfileForm({ onSubmit, mobileNumber, homeNumber, preferredName, personalPronoun, homeAddress, aptSuiteUnit, preferredCommunication }) {
  const classes = useStyles();
  const { t } = useTranslation('labels');
  const [isEditable, setIsEditable] = useState(false);
  const { handleSubmit, control, watch, reset, getValues, setError, setValue } = useForm({
    mode: 'onSubmit',
    defaultValues: { mobileNumber, homeNumber, preferredName, personalPronoun, homeAddress, aptSuiteUnit, preferredCommunication },
  });

  useEffect(() => {
    setValue('mobileNumber', mobileNumber);
    setValue('homeNumber', homeNumber);
    setValue('homeAddress', homeAddress);
    setValue('preferredName', preferredName);
    setValue('personalPronoun', personalPronoun);
    setValue('preferredCommunication', preferredCommunication);
    setValue('aptSuiteUnit', aptSuiteUnit);
  }, [mobileNumber, homeNumber, preferredName, personalPronoun, homeAddress, preferredCommunication, aptSuiteUnit]);
  const mapErrorToForm = useReactHookFormErrorMap(getValues, setError, '');
  const mobileNumberWatch = watch('mobileNumber');
  const homeNumberWatch = watch('homeNumber');

  const { data: preferredCommunicationOptions } = useFetchPreferredCommunications({
    isMobileNumberFilled: !!mobileNumberWatch,
    isHomeNumberFilled: !!homeNumberWatch,
    isHomeNumberVisible: true,
    isMobileNumberVisible: true,
  });

  return (
    <form
      data-testid="profile-form"
      className={classes.profileForm}
      onSubmit={handleSubmit((data) => {
        onSubmit(data)
          .then(() => setIsEditable(false))
          .catch((error) => {
            mapErrorToForm(error, { contactError: 'preferredCommunication' });
          });
      })}
    >
      <FormButtons
        isEditMode={isEditable}
        onReset={() => reset({ mobileNumber, homeNumber, preferredName, personalPronoun, homeAddress, preferredCommunication, aptSuiteUnit })}
        onModeUpdate={setIsEditable}
      >
        <div className={classes.multiInputsWrapper}>
          <div className={classes.field}>
            <TextFieldControl
              name="preferredName"
              rules={{
                maxLength: {
                  value: 25,
                  message: t('you_cant_use_more_than_number_characters', { number: 25 }),
                },
              }}
              control={control}
              labelText={t('preferred_name')}
              type="text"
              isEditMode={isEditable}
            />
          </div>

          <div className={classes.field}>
            <PronounSelectControl name="personalPronoun" control={control} isEditMode={isEditable} pronoun={personalPronoun} />
          </div>
        </div>
        <div className={classes.multiInputsWrapper}>
          <div className={classes.field}>
            <TextFieldControl
              rules={{
                required: {
                  value: true,
                  message: t('please_fill_in_this_field'),
                },
                maxLength: {
                  value: 20,
                  message: t('you_cant_use_more_than_number_characters', { number: 20 }),
                },
              }}
              name="mobileNumber"
              control={control}
              labelText={t('mobile_number')}
              type="number"
              isEditMode={isEditable}
            />
          </div>
          <div className={classes.field}>
            <TextFieldControl
              name="homeNumber"
              rules={{
                maxLength: {
                  value: 20,
                  message: t('you_cant_use_more_than_number_characters', { number: 20 }),
                },
              }}
              control={control}
              labelText={t('home_number')}
              type="number"
              isEditMode={isEditable}
            />
          </div>
        </div>
        <div className={classes.preferredCommunication}>
          <CheckboxesGroupControl
            name="preferredCommunication"
            control={control}
            labelText={t('preferred_method_of_communication')}
            label={
              <>
                <InputLabel text={t('choose_your_preferred_method_s_of_communication')} required />
                <Typography className={classes.preferredCommunicationLabel} variant="body2" component="p">
                  {t('the_selection_s_below_will_be_used_for_automated_appointment_reminders_and_notifications')}
                </Typography>
              </>
            }
            options={preferredCommunicationOptions}
            optionClasses={classes.checkboxOption}
            isEditMode={isEditable}
          />
        </div>
        <div className={classes.addressWrapper}>
          <div className={classes.field}>
            <AddressAutocompleteControl name="homeAddress" control={control} labelText={t('address')} isEditMode={isEditable} />
          </div>
          <div className={classes.field}>
            <TextFieldControl name="aptSuiteUnit" control={control} labelText={t('apt_suite_flr')} isEditMode={isEditable} />
          </div>
        </div>
      </FormButtons>
    </form>
  );
}

ProfileForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  mobileNumber: PropTypes.string,
  homeNumber: PropTypes.string,
  preferredName: PropTypes.string,
  personalPronoun: PropTypes.string,
  homeAddress: PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(PropTypes.any)]),
  aptSuiteUnit: PropTypes.string,
  preferredCommunication: PropTypes.arrayOf(PropTypes.string),
};

ProfileForm.defaultProps = {
  mobileNumber: '',
  homeNumber: '',
  preferredName: '',
  personalPronoun: '',
  homeAddress: null,
  aptSuiteUnit: '',
  preferredCommunication: [],
};

export default ProfileForm;
